/**
 * The run's event log: one line per event (from `@ab/session`'s
 * `describeEvents`), newest at the bottom. Collapsed it shows its last
 * `uiTiming.logLinesPortrait` lines in portrait and `logLinesLandscape` in
 * landscape (integration plan slice 4: the HUD's height budget); a tap on it,
 * or its toggle, expands it to scroll back (`ui.log.expand` /
 * `ui.log.collapse`).
 */
import { type CSSProperties, useEffect, useRef, useState } from 'react';
import type { Strings } from '@ab/session';
import { text } from './text.ts';
import { uiTiming } from './timing.ts';
import './ui.css';

export type EventLogProps = { readonly lines: readonly string[]; readonly strings: Strings };

const lineCounts = { '--ab-log-lines-portrait': uiTiming.logLinesPortrait, '--ab-log-lines-landscape': uiTiming.logLinesLandscape } as CSSProperties;

export const EventLog = ({ lines, strings }: EventLogProps) => {
  const list = useRef<HTMLOListElement>(null);
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    const el = list.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lines, expanded]);
  const toggle = (): void => {
    setExpanded((x) => !x);
  };
  return (
    <section className={`ab-log${expanded ? ' ab-log-expanded' : ''}`} aria-label={text(strings, 'ui.log.title')} style={lineCounts} data-testid="log">
      <ol ref={list} data-testid="event-log" onClick={toggle}>
        {lines.map((line, i) => (
          // Lines are only ever appended, so the index is a stable key.
          <li key={i}>{line}</li>
        ))}
      </ol>
      <button type="button" className="ab-log-toggle" aria-expanded={expanded} aria-label={text(strings, expanded ? 'ui.log.collapse' : 'ui.log.expand')} onClick={toggle} />
    </section>
  );
};
